import {FC} from 'react'
import {Container} from 'react-bootstrap'
import {useTranslation} from 'react-i18next'
import {useLocation, useNavigate} from 'react-router-dom'
import {useFriendRequestCount} from '../../contexts/FriendRequestCountContext'
import {
  BarChartLine,
  BarChartLineFill,
  Building,
  BuildingFill,
  CalendarEvent,
  CalendarEventFill,
  CardText,
  Gear,
  GearFill,
  House,
  HouseFill,
  People,
  PeopleFill,
  FilePerson,
  FilePersonFill,
  Sliders,
  Sliders2,
  ListOl,
  FileText,
  FileTextFill,
  Icon,
  Trophy,
  TrophyFill
} from 'react-bootstrap-icons'
import {useUser} from '../../contexts/UserContext'
import {getBottomBarItems, BottomBarItem} from '../navigation/utils/roleHierarchy'
import styles from './Footer.module.css'

const ICON_MAP_OUTLINE: Record<string, Icon> = {
  dashboard: House,
  scorecards: CardText,
  events: CalendarEvent,
  competitions: Trophy,
  rankings: ListOl,
  friends: People,
  stats: BarChartLine,
  profile: FilePerson,
  administration: Gear,
  settings: Sliders,
  users: People,
  tenants: Building,
  reports: FileText,
  clubs: Building
}

const ICON_MAP_FILLED: Record<string, Icon> = {
  dashboard: HouseFill,
  scorecards: CardText,
  events: CalendarEventFill,
  competitions: TrophyFill,
  rankings: ListOl,
  friends: PeopleFill,
  stats: BarChartLineFill,
  profile: FilePersonFill,
  administration: GearFill,
  settings: Sliders2,
  users: PeopleFill,
  tenants: BuildingFill,
  reports: FileTextFill,
  clubs: BuildingFill
}

const Footer: FC = () => {
  const {t} = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const {user} = useUser()
  const {count: friendRequestCount} = useFriendRequestCount()

  const userRoles = user?.roles || []
  const bottomBarItems: BottomBarItem[] = getBottomBarItems(userRoles)
  const itemCount = bottomBarItems.length

  return (
    <footer className={styles.footer}>
      <Container fluid className="p-0">
        <nav
          className={styles.mobileNav}
          data-item-count={itemCount}
          role="navigation"
          aria-label="Mobile navigation"
        >
          {bottomBarItems.map((item) => {
            const isActive = location.pathname === item.path ||
              location.pathname.startsWith(item.path + '/')
            const Icon = isActive ? ICON_MAP_FILLED[item.key] : ICON_MAP_OUTLINE[item.key]

            return (
              <a
                key={item.key}
                href={item.path}
                className={`${styles.navItem} ${isActive ? styles.active : ''}`}
                aria-current={isActive ? 'page' : undefined}
                onClick={(e) => {
                  e.preventDefault()
                  navigate(item.path)
                }}
              >
                <span style={{position: 'relative', display: 'inline-flex'}}>
                  {Icon && <Icon size={18} aria-hidden="true"/>}
                  {item.key === 'friends' && friendRequestCount > 0 && (
                    <span
                      style={{
                        position: 'absolute',
                        top: -2,
                        right: -4,
                        width: 8,
                        height: 8,
                        backgroundColor: 'var(--bs-danger)',
                        borderRadius: '50%'
                      }}
                      aria-label="New friend requests"
                    />
                  )}
                </span>
                <span className={styles.navLabel}>
                  {t(`navigation.roles.${item.role}.items.${item.key}`)}
                </span>
              </a>
            )
          })}
        </nav>
      </Container>
    </footer>
  )
}

export default Footer
